import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const formatNumber = (value, fractionDigits = 2) =>
	new Intl.NumberFormat('en-GB', {
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	}).format(value ?? 0);

const computeTotals = (data) => {
	return data.reduce(
		(acc, cur) => {
			acc.kg += cur.killograms ?? 0;
			acc.boxes += cur.boxes ?? 0;
			return acc;
		},
		{ kg: 0, boxes: 0 },
	);
};

export const exportIngredientRequirementsPdf = (data, params, opts = {}) => {
	if (!Array.isArray(data) || data.length === 0) return;

	const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
	const marginX = 40;
	const marginY = 36;
	const now = new Date();
	const locale = 'en-GB';
	const timestamp = new Intl.DateTimeFormat(locale, {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	}).format(now);

	const companyName = opts.companyName || 'Alberts NV';
	const companyPhone = opts.companyPhone || '+32 3 361 76 22';
	const companyEmail = opts.companyEmail || 'team@alberts.be';
	const companyAddress = opts.companyAddress || 'Bijkhoevelaan 32c, 2110 Wijnegem, Belgium';
	const companyVat = opts.companyVat || 'BE 0635.874.689';
	const disclaimer = opts.disclaimer || 'Indicative forecast based on selected recipes and parameters.';
	const totalPagesExp = '{total_pages_count_string}';

	// We render header elements per page in didDrawPage; avoid duplicating here.
	let y = marginY;

	const head = [['Ingredient ID', 'Ingredient Name', 'Kg', 'Boxes']];
	const body = data.map((item) => [
		item.ingredientId,
		item.ingredientName,
		`${formatNumber(item.killograms)} kg`,
		item.boxes,
	]);

	const totals = computeTotals(data);

	const headerReservedTop = 180;
	const footerReservedBottom = 70;

	autoTable(doc, {
		head,
		body,
		// Reserve header/footer space for each page
		startY: headerReservedTop,
		margin: { top: headerReservedTop, bottom: footerReservedBottom },
		styles: { font: 'helvetica', fontSize: 10, cellPadding: 6 },
		headStyles: { fillColor: [66, 66, 66], textColor: [255, 255, 255] },
		columnStyles: {
			0: { cellWidth: 180 },
			1: { cellWidth: 180 },
			2: { cellWidth: 80, halign: 'left' },
			3: { cellWidth: 80, halign: 'left' },
		},
		didDrawPage: (dataHook) => {
			const pageWidth = doc.internal.pageSize.getWidth();
			const pageHeight = doc.internal.pageSize.getHeight();

			// Header logo/title (redraw on each page for consistency)
			let headerY = marginY - 6;
			if (opts.logoDataUrl) {
				try {
					doc.addImage(opts.logoDataUrl, 'PNG', marginX - 8, marginY - 8, 160, 46);
				} catch (e) {
					// ignore
				}
			}
			doc.setFont('helvetica', 'bold');
			doc.setFontSize(12);
			doc.text('Ingredient Forecast', marginX, headerY + 64);

			doc.setFont('helvetica', 'normal');
			doc.setFontSize(9);
			doc.text(`Generated: ${timestamp}`, pageWidth - marginX, headerY + 64, { align: 'right' });

			// Meta lines under title
			const machineLabel = `Machine: ${opts.machineName || params.machineId}`;
			const customerLabel = opts.customerName ? `Customer: ${opts.customerName}` : null;
			const filtersLabel = `Recipes per day: ${params.recipesPerDay}  •  Days to cover: ${params.daysToCover}`;
			let metaLeft = machineLabel;
			if (customerLabel) metaLeft = `${metaLeft}  •  ${customerLabel}`;
			doc.text(metaLeft, marginX, headerY + 80);
			doc.text(filtersLabel, marginX, headerY + 94);

			if (dataHook.pageNumber === 1 && disclaimer) {
				doc.setFont('helvetica', 'normal');
				doc.setFontSize(9);
				doc.setTextColor(120);
				const discLines = doc.splitTextToSize(disclaimer, pageWidth - marginX * 2);
				const disclaimerY = 172; // slightly above the table start (headerReservedTop - 8)
				doc.text(discLines, marginX, disclaimerY);
			}

			// Footer
			const str = `Page ${doc.internal.getNumberOfPages()} of ${totalPagesExp}`;
			doc.setFontSize(9);
			doc.setTextColor(100);
			const footerLeftLine1 = `${companyName}  •  ${companyPhone}  •  ${companyEmail}`;
			const footerLeftLine2 = `${companyAddress}  •  ${companyVat}`;
			doc.text(footerLeftLine1, marginX, pageHeight - 36);
			doc.text(footerLeftLine2, marginX, pageHeight - 24);
			doc.text(str, pageWidth - 2, pageHeight - 36, { align: 'right' });
		},
		didParseCell: (hookData) => {
			const rowIndex = hookData.row.index;
			// Determine if this is a body row and needs dimming
			if (hookData.section === 'body') {
				const rowData = data[rowIndex];
				const isZero = (rowData?.killograms ?? 0) === 0 && (rowData?.boxes ?? 0) === 0;
				if (isZero) {
					hookData.cell.styles.textColor = [140, 140, 140];
				}
			}
		},
		foot: [
			[
				{ content: 'Totals', colSpan: 2 },
				{ content: `${formatNumber(totals.kg)} kg`, styles: { halign: 'left' } },
				{ content: `${totals.boxes}`, styles: { halign: 'left' } },
			],
		],
		footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
	});

	// Replace total pages placeholder
	if (typeof doc.putTotalPages === 'function') {
		doc.putTotalPages(totalPagesExp);
	}

	const safeName = (opts.machineName || params.machineId || 'Machine').replace(/[^a-z0-9_-]/gi, '_');
	const yyyy = now.getFullYear();
	const mm = String(now.getMonth() + 1).padStart(2, '0');
	const dd = String(now.getDate()).padStart(2, '0');
	doc.save(`Alberts_Ingredient_Forecast_${safeName}_${yyyy}${mm}${dd}.pdf`);
};

export default exportIngredientRequirementsPdf;
